import {
  isDefinedString,
  isNullOrUndefined,
  isNumber,
  isPositiveNumber,
  reduce
} from "et3-type-guards";

import { apiCallGetUsersData } from "../../../api/user-api/api-call-get-user-data";
import configuration from "../../../configuration/keys";
import { COMMENTS_DEFAULT_NUMBER } from "../../../constants/input-entries-constants";
import getCorePersonalStore from "../../core/core-personal-store";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import getUserAdvancedStore from ".";
import getUserLoadMoreStore from "./loadMoreByUserID";
import { isValidReferralOrPromoCodeCode } from "../../../helpers/et3-data-helper";
import { apiCallGetUserQuizData } from "../../../api/user-api/api-call-get-user-quiz-data";
import { TUserQuestionsAnswers } from "../../../Types";
import {
  reducerToEmptyStringIfUndefined,
  reducerToZeroIfUndefined
} from "../../../helpers/et3-picker-data-helper";

const getUsersDataFromDatabase = async () => {
  const userAdvancedStore = getUserAdvancedStore();
  userAdvancedStore.resetData();
  const searchType = userAdvancedStore.searchType.get();
  const condition = userAdvancedStore.searchByInput.get();
  let value = `${userAdvancedStore.advancedSearchInput.get()}`.trim();

  const userName = !isDefinedString(
    userAdvancedStore.criteriaSearchUserName.get()
  )
    ? " "
    : userAdvancedStore.criteriaSearchUserName.get();

  const userDescription = !isDefinedString(
    `${userAdvancedStore.criteriaSearchDescription.get()}`.trim()
  )
    ? " "
    : `${userAdvancedStore.criteriaSearchDescription.get()}`.trim();

  const userPeerDescription = !isDefinedString(
    `${userAdvancedStore.criteriaSearchPeerDescription.get()}`.trim()
  )
    ? " "
    : `${userAdvancedStore.criteriaSearchPeerDescription.get()}`.trim();

  const userNationality = reducerToEmptyStringIfUndefined(
    userAdvancedStore.criteriaSearchNationality.get().value
  );
  const userResidenceCountry = reducerToEmptyStringIfUndefined(
    userAdvancedStore.criteriaSearchResidenceCountry.get().value
  );
  const userAccountCreatedOn = reducerToEmptyStringIfUndefined(
    userAdvancedStore.criteriaSearchAccountCreatedOn.get().value
  );
  const userAge = reducerToZeroIfUndefined(
    parseInt(userAdvancedStore.criteriaSearchAge.get().value, 10)
  );
  const userHeight = reducerToZeroIfUndefined(
    parseInt(userAdvancedStore.criteriaSearchHeight.get().value, 10)
  );
  const userWeight = reducerToZeroIfUndefined(
    parseInt(userAdvancedStore.criteriaSearchWeight.get().value, 10)
  );
  if (
    searchType === "option_search" &&
    (isNullOrUndefined(value) || !isDefinedString(value))
  ) {
    return;
  }

  if (
    condition === "referralCode" &&
    !isValidReferralOrPromoCodeCode(value) &&
    searchType === "option_search"
  ) {
    getCorePopUpStore().showAlertMessage("WRONG_REFFERAL_CODE");
    return;
  }

  if (
    condition === "profileId" &&
    !userAdvancedStore.checkIfValidProfileID(value)
  ) {
    return;
  }

  const adminUserId = getCorePersonalStore().user.get().id;
  if (
    !isNumber(adminUserId) ||
    (searchType === "option_search" &&
      !isDefinedString(condition) &&
      !isDefinedString(value)) ||
    (searchType === "criteria_search" &&
      !isDefinedString(userName) &&
      !isDefinedString(userNationality) &&
      !isDefinedString(userResidenceCountry) &&
      !isPositiveNumber(userAge) &&
      !isPositiveNumber(userHeight) &&
      !isPositiveNumber(userWeight) &&
      !isDefinedString(userDescription) &&
      !isDefinedString(userPeerDescription) &&
      !isDefinedString(userAccountCreatedOn))
  ) {
    getCorePopUpStore().showAlertMessage("WRONG_INPUT");
    return;
  }
  if (searchType === "criteria_search") {
    value = " ";
  }
  getCoreUIStore().setIsLoading("USER_ADVANCED", true);

  try {
    const { data, status, code } = await apiCallGetUsersData({
      searchType,
      condition,
      value,
      userName,
      userNationality,
      userResidenceCountry,
      userAge,
      userHeight,
      userWeight,
      userDescription,
      userPeerDescription,
      userAccountCreatedOn
    });
    if (status === "OK") {
      userAdvancedStore.criteriaSearchDescription.set(userDescription.trim());
      userAdvancedStore.criteriaSearchPeerDescription.set(
        userPeerDescription.trim()
      );
      for (let i = 0; i < data.length; i++) {
        const userId = reduce(() => data[i].userId, -1);
        const { pictures } =
          await getUserAdvancedStore().getUserPictureFromAlbum(userId);
        const imageArray = pictures.map((picture) => {
          let pictureFile = `${configuration.file_center}${picture.pic_resized}`;
          if (picture.pic_resized === "") {
            pictureFile = "";
          }
          const pictureStatus = reduce(() => picture.status, "active");
          return {
            imageSource: pictureFile,
            profilePictureStatus: `${picture.accepted}`,
            selfieVerified: `${picture.selfie_verified}`,
            isActive: pictureStatus === "active",
            id: `${picture.id}`
          };
        });
        const loadMoreValue = reduce(
          () => getUserLoadMoreStore(userId).loadMore.get(),
          COMMENTS_DEFAULT_NUMBER
        );
        const { userAdminComments, commentsNumber } =
          await getUserAdvancedStore().getUserComments(userId, loadMoreValue);
        const isAnyKhattabaAdmin =
          getCorePersonalStore().isAnyKhattabaAdmin.get();

        let quizData: TUserQuestionsAnswers[] = [];
        if (isAnyKhattabaAdmin) {
          const { data } = await apiCallGetUserQuizData({
            userId
          });
          quizData = data;
        }

        const index = imageArray.findIndex((image) => {
          return (
            image.selfieVerified === "Y" &&
            image.profilePictureStatus === "Y" &&
            image.isActive
          );
        });
        const finalIndex = index === -1 ? 0 : index;

        const parsedData = {
          ...data[i],
          mainPictureIndex: finalIndex,
          pictures: imageArray,
          comments: userAdminComments,
          commentsNumber: commentsNumber,
          quizData
        };
        if (
          isPositiveNumber(userId) &&
          userId > 0 &&
          getUserAdvancedStore().userIDs.get().indexOf(userId) === -1
        ) {
          getUserAdvancedStore().userIDs.get().push(userId);
          getUserAdvancedStore().userData.set(userId, parsedData);
        }
      }
      const firstUserID = getUserAdvancedStore().userIDs.get()[0];
      getUserAdvancedStore().currentUserId.set(firstUserID);
      getUserAdvancedStore().currentIndex.set(0);
      const mainPictureIndex = getUserAdvancedStore().userData.get(
        getUserAdvancedStore().currentUserId.get()
      ).mainPictureIndex;
      getUserAdvancedStore().userAlbumIndex.set(mainPictureIndex);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
    getCoreUIStore().setIsLoading("USER_ADVANCED", false);
  } catch (error) {}
};

export default getUsersDataFromDatabase;
